import { Building, BuildingMetadata, Property, Unit } from '../../src/app/shared/models/entity';
import {
  auditFields,
  createMany,
  cycle,
  FixtureBuilder,
  FixtureFactory,
  fixtureId,
} from './fixture.utils';

// -----------------------------------------------------------------------------------------------------
// @ SAMPLE POOLS
// -----------------------------------------------------------------------------------------------------

const BUILDING_NAMES: readonly string[] = [
  'Vorderhaus',
  'Hinterhaus',
  'Seitenflügel',
  'Gartenhaus',
];

const CONSTRUCTION_YEARS: readonly number[] = [1904, 1958, 1972, 1996, 2014];

// -----------------------------------------------------------------------------------------------------
// @ FACTORY
// -----------------------------------------------------------------------------------------------------

/**
 * Builds a Building with realistic defaults. Every field can be overridden.
 *
 * @param overrides - The fields to override.
 * @param index - The zero-based index, driving the deterministic id and the sample rotation.
 * @returns {Building} A fresh Building instance.
 */
export const createBuilding: FixtureFactory<Building> = (
  overrides: Partial<Building> = {},
  index: number = 0,
): Building => {
  return new Building({
    id: fixtureId('building', index),
    ...auditFields(index),
    name: cycle(BUILDING_NAMES, index),
    propertyId: '',
    propertyName: '',
    constructionYear: cycle(CONSTRUCTION_YEARS, index),
    street: 'Lindenstraße',
    houseNumber: `${12 + index}`,
    unitCount: 0,
    metadata: new BuildingMetadata(),
    units: [],
    ...overrides,
  });
};

/**
 * Builds a list of Buildings.
 *
 * @param count - How many buildings to build.
 * @param overrides - Static overrides, or a callback returning overrides per index.
 * @returns {Building[]} The built buildings.
 */
export function createBuildings(
  count: number,
  overrides?: Partial<Building> | ((index: number) => Partial<Building>),
): Building[] {
  return createMany(createBuilding, count, overrides);
}

// -----------------------------------------------------------------------------------------------------
// @ BASE FIXTURES
// -----------------------------------------------------------------------------------------------------

export const DEFAULT_BUILDING: Building = createBuilding();

// -----------------------------------------------------------------------------------------------------
// @ SCENARIO FIXTURES
// -----------------------------------------------------------------------------------------------------

/** An Altbau with many units — the typical WEG case. */
export const BUILDING_ALTBAU: Building = createBuilding(
  { name: 'Vorderhaus', constructionYear: 1904, unitCount: 12 },
  0,
);

/** A new build — fewer units, recent construction year. */
export const BUILDING_NEUBAU: Building = createBuilding(
  { name: 'Neubau Ost', constructionYear: 2014, unitCount: 6 },
  1,
);

/** A building without units yet — for empty-state paths. */
export const BUILDING_WITHOUT_UNITS: Building = createBuilding({ unitCount: 0, units: [] }, 2);

// -----------------------------------------------------------------------------------------------------
// @ BUILDER
// -----------------------------------------------------------------------------------------------------

export class BuildingBuilder extends FixtureBuilder<Building> {
  // -----------------------------------------------------------------------------------------------------
  // @ CONSTRUCTOR
  // -----------------------------------------------------------------------------------------------------

  constructor(index: number = 0) {
    super(createBuilding({}, index));
  }

  // -----------------------------------------------------------------------------------------------------
  // @ PUBLIC METHODS
  // -----------------------------------------------------------------------------------------------------

  /**
   * Places the building in a property — wires the id, the denormalized name, the address
   * and optionally the relation itself.
   *
   * @param property - The property the building belongs to.
   * @param withRelation - Whether to attach the property instance itself. Off by default to keep the graph flat.
   * @returns {this} The builder, for chaining.
   */
  inProperty(property: Property, withRelation: boolean = false): this {
    return this.with({
      propertyId: property.id,
      propertyName: property.name,
      street: property.street,
      houseNumber: property.houseNumber,
      property: withRelation ? property : undefined,
    });
  }

  /**
   * Attaches the units and keeps `unitCount` in sync.
   *
   * @param units - The units of the building.
   * @returns {this} The builder, for chaining.
   */
  withUnits(units: Unit[]): this {
    return this.with({ units, unitCount: units.length });
  }

  /**
   * Sets the construction year.
   *
   * @param year - The construction year.
   * @returns {this} The builder, for chaining.
   */
  builtIn(year: number): this {
    return this.with({ constructionYear: year });
  }

  // -----------------------------------------------------------------------------------------------------
  // @ OVERRIDE METHODS
  // -----------------------------------------------------------------------------------------------------

  override build(): Building {
    return new Building(this.draft);
  }
}
